/**
 * ToastProvider - Top-right positioned toast notification system
 *
 * Manages toast notifications with slide-in animations from the right
 * using MUI Snackbar and Framer Motion for smooth animations
 */

import React, { useCallback, useEffect, useState } from 'react'
import { Alert, AlertTitle } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import { X } from 'lucide-react'

import { useMessaging } from '../../contexts/MessagingContext'
import type { Message } from '../../types/messaging'
import {
  DEFAULT_TOAST_CONFIG,
  MESSAGE_TYPE_CONFIG,
} from '../../types/messaging'

import { messageTypeIcon } from './messageIcons'

interface ToastItemProps {
  message: Message
  onClose: (messageId: string) => void
  onCommandClick?: (messageId: string, command: () => void) => void
}

const ToastItem = ({
  message,
  onClose,
  onCommandClick,
}: ToastItemProps): React.JSX.Element | null => {
  const [isVisible, setIsVisible] = useState(true)

  const handleClose = useCallback(() => {
    setIsVisible(false)
    // Delay actual removal to allow exit animation
    setTimeout(() => {
      if (message.id) {
        onClose(message.id)
      }
    }, 200)
  }, [message.id, onClose])

  // Auto-dismiss non-persistent toasts
  useEffect(() => {
    if (!message.persistent && message.timestamp) {
      const timer = setTimeout(() => {
        handleClose()
      }, DEFAULT_TOAST_CONFIG.duration)

      return () => clearTimeout(timer)
    }
  }, [message.persistent, message.timestamp, handleClose])

  const handleCommandClick = useCallback(() => {
    if (message.command && message.id && onCommandClick) {
      onCommandClick(message.id, message.command.onClick)
    }
  }, [message.command, message.id, onCommandClick])

  // Shared severity map — same glyph the bell row for this message shows.
  // (It also fixes `error`, which used to fall through to the info glyph
  // because only `failure` had a case here.)
  const SeverityIcon = messageTypeIcon(message.type)

  const config = MESSAGE_TYPE_CONFIG[message.type]

  if (!isVisible) return null

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className="mb-2 w-full max-w-md"
    >
      <Alert
        severity={config.color}
        variant="filled"
        className="shadow-lg"
        icon={
          <div className="text-white">
            <SeverityIcon className="w-5 h-5 text-white" />
          </div>
        }
        action={
          <button
            onClick={handleClose}
            className="ml-2 p-1 rounded-full hover:bg-black/10 transition-colors"
            aria-label="Close notification"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        }
      >
        {message.title && (
          <AlertTitle className="font-semibold text-white">
            {message.title}
          </AlertTitle>
        )}
        <div className="flex flex-col gap-2">
          <div className="text-white">{message.message}</div>
          {message.command && (
            <button
              onClick={handleCommandClick}
              className="text-left underline hover:no-underline transition-all text-white font-medium"
            >
              {message.command.text}
            </button>
          )}
        </div>
      </Alert>
    </motion.div>
  )
}

interface ToastContainerProps {
  messages: Message[]
  onClose: (messageId: string) => void
}

const ToastContainer = ({
  messages,
  onClose,
}: ToastContainerProps): React.JSX.Element => {
  const handleCommandClick = useCallback(
    (messageId: string, command: () => void) => {
      command()
      // Optionally close the toast after command execution
      onClose(messageId)
    },
    [onClose],
  )

  // Limit the number of visible toasts
  const visibleMessages = messages.slice(0, DEFAULT_TOAST_CONFIG.maxToasts)

  return (
    <div
      className="fixed top-5 right-5 z-[9999] flex flex-col items-end"
      style={{
        maxWidth: '400px',
        pointerEvents: 'none', // Allow clicks to pass through container
      }}
    >
      <AnimatePresence mode="popLayout">
        {visibleMessages.map(message => (
          <div
            key={message.id}
            style={{ pointerEvents: 'auto' }} // Re-enable clicks on toast items
          >
            <ToastItem
              message={message}
              onClose={onClose}
              onCommandClick={handleCommandClick}
            />
          </div>
        ))}
      </AnimatePresence>
    </div>
  )
}

/**
 * ToastProvider - Main toast notification provider component
 *
 * Renders toast notifications in the top-right corner with slide-in animations
 * Integrates with MessagingContext to display toast-targeted messages
 */
// @purpose Renders top-right animated toast notifications from MessagingContext's toast messages
export const ToastProvider = (): React.JSX.Element => {
  const { toastMessages, removeToastMessage } = useMessaging()

  const handleCloseToast = useCallback(
    (messageId: string) => {
      removeToastMessage(messageId)
    },
    [removeToastMessage],
  )

  return <ToastContainer messages={toastMessages} onClose={handleCloseToast} />
}

export default ToastProvider
